import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import './Sidebar.css';
import { FaCompass, FaSignOutAlt, FaCog, FaBook, FaChevronLeft, FaChevronRight, FaRobot, FaUsers, FaBuilding, FaMagic } from 'react-icons/fa';
import { GiRadarSweep } from "react-icons/gi";
import { usePlan } from '../contexts/PlanContext';
import veloxIcon from '../assets/Velox-icon-b&w.png';


function SidebarItem({ icon, label, selected, onClick, path, index, collapsed }) {
  const navigate = useNavigate();

  const handleClick = useCallback((e) => {
    e.preventDefault();
    if (onClick) {
      onClick(e);
      return;
    }
    if (path) {
      navigate(path);
    }
  }, [onClick, path, navigate]);

  return (
    <div
      className={`sidebar-item${selected ? ' selected' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        gap: collapsed ? 0 : 2, 
        justifyContent: 'center', 
        width: '100%', 
        padding: '12px 2px',
        minHeight: collapsed ? 48 : 64
      }}
    >
      <span className="sidebar-icon">{icon}</span>
      {!collapsed && <span className="sidebar-label">{label}</span>}
    </div>
  );
}

function RadarSidebarItem({ icon, label, selected, collapsed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const itemRef = useRef(null);
  const menuRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const radarScreens = [
    { label: 'Accounts', path: '/app/radar/accounts' },
    { label: 'Contacts', path: '/app/radar/contacts' },
    { label: 'Transcript Analyzer', path: '/app/radar/transcripts' },
    { label: 'Buyer Cards', path: '/app/radar/buyer-cards' }
  ];

  const handleMenuClick = useCallback((path) => {
    navigate(path);
    setIsHovered(false);
  }, [navigate]);

  // Calculate menu position when hovered or on scroll
  useEffect(() => {
    const updatePosition = () => {
      if (isHovered && itemRef.current) {
        const rect = itemRef.current.getBoundingClientRect();
        setMenuPosition({
          top: rect.top,
          left: rect.right + 4
        });
      }
    };

    updatePosition();

    if (isHovered) {
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isHovered]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        itemRef.current && 
        menuRef.current &&
        !itemRef.current.contains(event.target) &&
        !menuRef.current.contains(event.target)
      ) {
        setIsHovered(false);
      }
    };

    if (isHovered) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isHovered]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleItemClick = useCallback((e) => {
    e.preventDefault();
    // Navigate to default radar screen (Accounts)
    navigate('/app/radar/accounts');
  }, [navigate]);

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Use a small delay to allow moving to the menu
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 150);
  }, []);

  return (
    <>
      <div
        ref={itemRef}
        className={`sidebar-item${selected ? ' selected' : ''} radar-sidebar-item`}
        onClick={handleItemClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="button"
        tabIndex={0}
        style={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          gap: collapsed ? 0 : 2, 
          justifyContent: 'center', 
          width: '100%', 
          padding: '12px 2px',
          minHeight: collapsed ? 48 : 64,
          position: 'relative'
        }}
      >
        <span className="sidebar-icon">{icon}</span>
        {!collapsed && <span className="sidebar-label">{label}</span>}
      </div>
      {isHovered && createPortal(
        <div 
          ref={menuRef}
          className="radar-dropdown-menu"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {radarScreens.map((screen) => {
            const isScreenSelected = location.pathname === screen.path;
            return (
              <div
                key={screen.path}
                className={`radar-menu-item${isScreenSelected ? ' selected' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuClick(screen.path);
                }}
              >
                {screen.label}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

function CompassSidebarItem({ icon, label, selected, collapsed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const itemRef = useRef(null);
  const menuRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const compassScreens = [
    { label: 'Saved Notes', path: '/app/compass/saved-notes' },
    { label: 'Editor', path: '/app/compass/editor' }
  ];

  const handleMenuClick = useCallback((path) => {
    navigate(path);
    setIsHovered(false);
  }, [navigate]);

  // Calculate menu position when hovered or on scroll
  useEffect(() => {
    const updatePosition = () => {
      if (isHovered && itemRef.current) {
        const rect = itemRef.current.getBoundingClientRect();
        setMenuPosition({
          top: rect.top,
          left: rect.right + 4
        });
      }
    };

    updatePosition();

    if (isHovered) {
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isHovered]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        itemRef.current && 
        menuRef.current &&
        !itemRef.current.contains(event.target) &&
        !menuRef.current.contains(event.target)
      ) {
        setIsHovered(false);
      }
    };

    if (isHovered) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isHovered]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleItemClick = useCallback((e) => {
    e.preventDefault();
    // Navigate to default compass screen (Saved Notes)
    navigate('/app/compass/saved-notes');
  }, [navigate]);

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Use a small delay to allow moving to the menu
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 150);
  }, []);

  return (
    <>
      <div
        ref={itemRef}
        className={`sidebar-item${selected ? ' selected' : ''} compass-sidebar-item`}
        onClick={handleItemClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="button"
        tabIndex={0}
        style={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          gap: collapsed ? 0 : 2, 
          justifyContent: 'center', 
          width: '100%', 
          padding: '12px 2px',
          minHeight: collapsed ? 48 : 64,
          position: 'relative'
        }}
      >
        <span className="sidebar-icon">{icon}</span>
        {!collapsed && <span className="sidebar-label">{label}</span>}
      </div>
      {isHovered && createPortal(
        <div 
          ref={menuRef}
          className="compass-dropdown-menu"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {compassScreens.map((screen) => {
            const isScreenSelected = location.pathname === screen.path;
            return (
              <div
                key={screen.path}
                className={`compass-menu-item${isScreenSelected ? ' selected' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuClick(screen.path);
                }}
              >
                {screen.label}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

function WizardSidebarItem({ icon, label, selected, collapsed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const itemRef = useRef(null);
  const menuRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const wizardScreens = [
    { label: 'Custom Agents', path: '/app/wizard/custom-agents' }
    // Diagnostics disabled
    // { label: 'Diagnostics', path: '/app/wizard/diagnostics' }
  ];

  const handleMenuClick = useCallback((path) => {
    navigate(path);
    setIsHovered(false);
  }, [navigate]);

  // Calculate menu position when hovered or on scroll
  useEffect(() => {
    const updatePosition = () => {
      if (isHovered && itemRef.current) {
        const rect = itemRef.current.getBoundingClientRect();
        setMenuPosition({
          top: rect.top,
          left: rect.right + 4
        });
      }
    };

    updatePosition();

    if (isHovered) {
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isHovered]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        itemRef.current && 
        menuRef.current &&
        !itemRef.current.contains(event.target) &&
        !menuRef.current.contains(event.target)
      ) {
        setIsHovered(false);
      }
    };

    if (isHovered) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isHovered]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleItemClick = useCallback((e) => {
    e.preventDefault();
    // Navigate to default wizard screen (Custom Agents)
    navigate('/app/wizard/custom-agents');
  }, [navigate]);

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Use a small delay to allow moving to the menu
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 150);
  }, []);

  return (
    <>
      <div
        ref={itemRef}
        className={`sidebar-item${selected ? ' selected' : ''} wizard-sidebar-item`}
        onClick={handleItemClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="button"
        tabIndex={0}
        style={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          gap: collapsed ? 0 : 2, 
          justifyContent: 'center', 
          width: '100%', 
          padding: '12px 2px',
          minHeight: collapsed ? 48 : 64,
          position: 'relative'
        }}
      >
        <span className="sidebar-icon">{icon}</span>
        {!collapsed && <span className="sidebar-label">{label}</span>}
      </div>
      {isHovered && createPortal(
        <div 
          ref={menuRef}
          className="wizard-dropdown-menu"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {wizardScreens.map((screen) => {
            const isScreenSelected = location.pathname === screen.path || 
                                   (screen.path === '/app/wizard/custom-agents' && location.pathname.startsWith('/app/wizard/custom-agents'));
            return (
              <div
                key={screen.path}
                className={`wizard-menu-item${isScreenSelected ? ' selected' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuClick(screen.path);
                }}
              >
                {screen.label}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

function SidebarSeparator({ collapsed }) {
  return (
    <div 
      className="sidebar-separator"
      style={{
        width: collapsed ? '60%' : '80%',
        height: '1px',
        background: 'linear-gradient(90deg, transparent 0%, rgba(96,165,250, 0.6) 50%, transparent 100%)',
        margin: collapsed ? '8px 0' : '16px 0',
        alignSelf: 'center'
      }}
    />
  );
}

function SidebarSeparator2({ collapsed }) {
  return (
    <div 
      className="sidebar-separator2"
      style={{
        width: collapsed ? '60%' : '80%',
        height: '1px',
        background: 'linear-gradient(90deg, transparent 0%, rgba(219,234,254, 0.6) 50%, transparent 100%)',
        margin: collapsed ? '8px 0' : '16px 0',
        alignSelf: 'center'
      }}
    />
  );
}

// Feature flag: Set to true to enable Wizard module in the sidebar
const ENABLE_WIZARD_MODULE = true;

export default function Sidebar({ onLogout = () => {} }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { planType, loading } = usePlan();
  
  // Memoized modules configuration - organized into sections
  const { primaryModules, secondaryModules } = useMemo(() => {
    console.log('Sidebar: Creating modules array, planType =', planType, 'loading =', loading);
    
    // Primary section: Agent V, Radar, Compass, and Wizard
    const primaryModules = [
      { icon: <img src={veloxIcon} alt="Velox AI" style={{ width: '24px', height: '24px', filter: 'brightness(0) invert(1)' }} />, label: 'Agent V', path: '/app/agent-v' },
    ];

    // Add Radar module for all plans
    console.log('Sidebar: Adding Radar module for planType =', planType);
    primaryModules.push({ icon: <GiRadarSweep />, label: 'Radar', path: '/app/radar/accounts' });

    // Add Compass module
    primaryModules.push({ icon: <FaCompass />, label: 'Compass', path: '/app/compass/saved-notes' });

    // Add Wizard module for Team plan only (in primary section)
    // DISABLED: Set ENABLE_WIZARD_MODULE to true to re-enable
    if (ENABLE_WIZARD_MODULE && planType === 'Team') {
      console.log('Sidebar: Adding Wizard module for Team plan to primary section');
      primaryModules.push({ icon: <FaMagic />, label: 'Wizard', path: '/app/wizard/custom-agents' });
    }

    // Secondary section: Knowledge Hub
    const secondaryModules = [
      { icon: <FaBook />, label: 'Knowledge Hub', path: '/app/knowledge-hub' },
    ];

    console.log('Sidebar: Primary modules:', primaryModules.map(m => m.label));
    console.log('Sidebar: Secondary modules:', secondaryModules.map(m => m.label));
    
    return { primaryModules, secondaryModules };
  }, [planType, loading]);

  // Memoized bottom items
  const bottomItems = useMemo(() => [
    { icon: <FaCog />, label: 'Settings', path: '/app/settings' },
  ], []);

  // Memoize the collapse toggle to prevent unnecessary re-renders
  const toggleCollapse = useCallback(() => {
    setCollapsed(c => !c);
  }, []);

  console.log('Sidebar: location.pathname =', location.pathname, 'collapsed =', collapsed, 'planType =', planType, 'loading =', loading);

  return (
    <div className="sidebar" style={{ width: collapsed ? 48 : 100, transition: 'width 0.2s' }}>
      
      <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16, paddingRight: '8px' }}>
        <button
          onClick={toggleCollapse}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', fontSize: 18, padding: 4 }}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </button>
      </div>
      
      {/* Primary Section */}
      <div className="sidebar-section">
        {primaryModules.map((mod, idx) => {
          // Special handling for selection logic
          let isSelected = false;
          if (mod.path === '/app/agent-v') {
            // Agent V: match /app/agent-v, /app, or /app/
            isSelected = location.pathname === mod.path || 
                        location.pathname === '/app' || 
                        location.pathname === '/app/';
          } else if (mod.path === '/app/radar/accounts') {
            // Radar: match any path starting with /app/radar
            isSelected = location.pathname.startsWith('/app/radar');
          } else if (mod.path === '/app/compass/saved-notes') {
            // Compass: match any path starting with /app/compass
            isSelected = location.pathname.startsWith('/app/compass');
          } else if (mod.path === '/app/wizard/custom-agents') {
            // Wizard: match any path starting with /app/wizard
            isSelected = location.pathname.startsWith('/app/wizard');
          } else {
            // Default: exact match
            isSelected = location.pathname === mod.path;
          }
          
          // Special handling for Radar - use RadarSidebarItem with hover menu
          if (mod.path === '/app/radar/accounts') {
            return (
              <RadarSidebarItem
                key={mod.label}
                icon={mod.icon}
                label={mod.label}
                selected={isSelected}
                collapsed={collapsed}
              />
            );
          }
          
          // Special handling for Compass - use CompassSidebarItem with hover menu
          if (mod.path === '/app/compass/saved-notes') {
            return (
              <CompassSidebarItem
                key={mod.label}
                icon={mod.icon}
                label={mod.label}
                selected={isSelected}
                collapsed={collapsed}
              />
            );
          }
          
          // Special handling for Wizard - use WizardSidebarItem with hover menu
          // DISABLED: Only render if ENABLE_WIZARD_MODULE is true
          if (ENABLE_WIZARD_MODULE && mod.path === '/app/wizard/custom-agents') {
            return (
              <WizardSidebarItem
                key={mod.label}
                icon={mod.icon}
                label={mod.label}
                selected={isSelected}
                collapsed={collapsed}
              />
            );
          }
          
          return (
            <SidebarItem
              key={mod.label}
              icon={mod.icon}
              label={mod.label}
              selected={isSelected}
              path={mod.path}
              index={idx}
              collapsed={collapsed}
            />
          );
        })}
      </div>
  
      {/* First Separator */}
      <SidebarSeparator collapsed={collapsed} />
  
      {/* Secondary Section */}
      <div className="sidebar-section">
        {secondaryModules.map((mod, idx) => {
          // Special handling for Knowledge Hub
          let isSelected = false;
          if (mod.path === '/app/knowledge-hub') {
            // Knowledge Hub: match any path starting with /app/knowledge-hub
            isSelected = location.pathname.startsWith('/app/knowledge-hub');
          } else {
            // Default: exact match
            isSelected = location.pathname === mod.path;
          }
          
          return (
            <SidebarItem
              key={mod.label}
              icon={mod.icon}
              label={mod.label}
              selected={isSelected}
              path={mod.path}
              index={primaryModules.length + idx}
              collapsed={collapsed}
            />
          );
        })}
      </div>
  
      {/* Second Separator */}
      <SidebarSeparator2 collapsed={collapsed} />
  
      {/* Bottom Section */}
      <div className="sidebar-bottom">
        {bottomItems.map((item, idx) => (
          <SidebarItem
            key={item.label}
            icon={item.icon}
            label={item.label}
            selected={location.pathname === item.path}
            path={item.path}
            index={primaryModules.length + secondaryModules.length + idx}
            collapsed={collapsed}
          />
        ))}
        <SidebarItem 
          icon={<FaSignOutAlt />} 
          label="Logout" 
          onClick={onLogout}
          path={null}
          collapsed={collapsed}
        />
      </div>
    </div>
  );
}